import { useEffect, useState } from 'react';
import { isSafeSystemMessageImageUrl, sanitizeSystemMessageHtml, type SystemMessage } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppModal } from '../ui/AppModal';
import { FullscreenPinchImageViewer } from '../ui/FullscreenPinchImageViewer';
import { SystemMessageHtmlContent } from './SystemMessageHtmlContent';

type SystemMessageTickerProps = {
  items: SystemMessage[];
  rotationSeconds?: number;
  initialMessageId?: string;
  onInitialMessageOpened?: () => void;
  onOpenDetail?: (item: SystemMessage) => void;
};

export const SystemMessageDetailModal = ({
  item,
  onClose,
}: {
  item: SystemMessage;
  onClose: () => void;
}) => {
  const { t } = useLanguage();
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const safeHtml = sanitizeSystemMessageHtml(item.detailHtml || '');
  const lightboxOpen = lightboxSrc != null;

  useEffect(() => {
    setLightboxSrc(null);
  }, [item.id]);

  const handleOpenLightbox = (rawUrl: string): void => {
    const trimmed = rawUrl.trim();
    if (!isSafeSystemMessageImageUrl(trimmed)) {
      return;
    }
    setLightboxSrc(trimmed);
  };

  const handleCloseLightbox = (): void => {
    setLightboxSrc(null);
  };

  const handleCloseDetail = (): void => {
    if (lightboxOpen) {
      return;
    }
    onClose();
  };

  return (
    <>
      <AppModal
        open
        onClose={handleCloseDetail}
        title={item.subject}
        titleId="system-message-modal-title"
        closeAriaLabel={t('systemMessage.action.close')}
        closeDisabled={lightboxOpen}
        backdropZClass="z-50"
      >
        <p className="-mt-1 mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">{item.listDate}</p>
        <SystemMessageHtmlContent
          html={safeHtml}
          emptyFallback={t('systemMessage.detailEmpty')}
          onOpenImage={handleOpenLightbox}
        />
      </AppModal>
      <FullscreenPinchImageViewer
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        src={lightboxSrc ?? ''}
        alt={item.subject}
        dialogAriaLabel={t('systemMessage.image.enlargeAria')}
        closeLabel={t('common.closeViewer')}
      />
    </>
  );
};

export const SystemMessageTicker = ({
  items,
  rotationSeconds = 5,
  initialMessageId = '',
  onInitialMessageOpened,
  onOpenDetail,
}: SystemMessageTickerProps) => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [initialHandled, setInitialHandled] = useState(false);

  useEffect(() => {
    if (items.length <= 1 || modalOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, rotationSeconds * 1000);
    return () => window.clearTimeout(timer);
  }, [currentIndex, items.length, modalOpen, rotationSeconds]);

  useEffect(() => {
    if (currentIndex >= items.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, items.length]);

  useEffect(() => {
    if (initialHandled || initialMessageId.trim() === '' || items.length === 0) {
      return;
    }
    const targetIndex = items.findIndex((item) => item.id === initialMessageId);
    if (targetIndex < 0) {
      setInitialHandled(true);
      onInitialMessageOpened?.();
      return;
    }
    setCurrentIndex(targetIndex);
    setModalOpen(true);
    setInitialHandled(true);
    const target = items[targetIndex];
    if (target) {
      onOpenDetail?.(target);
    }
    onInitialMessageOpened?.();
  }, [initialHandled, initialMessageId, items, onInitialMessageOpened, onOpenDetail]);

  if (items.length === 0) {
    return null;
  }

  const current = items[currentIndex] ?? items[0]!;

  const handleOpen = (): void => {
    setModalOpen(true);
    onOpenDetail?.(current);
  };

  const handlePrev = (): void => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const handleNext = (): void => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <>
      <section
        className="mb-3 rounded-xl border border-black/5 px-3 py-2.5 shadow-sm"
        style={{ backgroundColor: current.tickerBgColor, color: current.tickerTextColor }}
        aria-label={t('systemMessage.ticker.title')}
      >
        <div className="mb-1 flex items-center justify-between gap-2 text-xs font-semibold opacity-80">
          <span className="truncate">{t('systemMessage.ticker.title')}</span>
          {items.length > 1 ? (
            <span className="shrink-0">
              {t('systemMessage.ticker.count', { current: currentIndex + 1, total: items.length })}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpen}
            className="min-w-0 flex-1 truncate text-left text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label={t('systemMessage.ticker.openDetails')}
            tabIndex={0}
          >
            {current.subject}
          </button>
          {items.length > 1 ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrev}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-black/10 opacity-90 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label={t('systemMessage.ticker.prev')}
                tabIndex={0}
              >
                <span aria-hidden>{'<'}</span>
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-black/10 opacity-90 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label={t('systemMessage.ticker.next')}
                tabIndex={0}
              >
                <span aria-hidden>{'>'}</span>
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {modalOpen ? (
        <SystemMessageDetailModal item={current} onClose={() => setModalOpen(false)} />
      ) : null}
    </>
  );
};
